import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Calendar,
  Clock,
  BarChart3,
  Zap,
  TrendingUp,
  Play,
  Pause,
  Plus,
  Trash2,
  RefreshCw,
  ChevronRight,
  TestTube,
  Award,
  Target,
  Lightbulb
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import api from '@/lib/api';

// Import extracted components
import { 
  TikTokIcon, 
  PlatformIcon, 
  PLATFORMS, 
  DAYS_OF_WEEK, 
  HOURS,
  EngagementHeatmap 
} from '../components/scheduler';

// A/B Test Card
const ABTestCard = ({ test, onViewResults, onStop }) => {
  const statusColors = {
    running: 'bg-green-500/20 text-green-400',
    completed: 'bg-blue-500/20 text-blue-400',
    cancelled: 'bg-gray-500/20 text-gray-400'
  };

  return (
    <Card className="glass-card">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-base">{test.name}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <PlatformIcon platform={test.platform} className="w-4 h-4" />
              {test.platform}
            </CardDescription>
          </div>
          <Badge className={statusColors[test.status] || statusColors.running}>
            {test.status === 'running' ? 'Em andamento' : 
             test.status === 'completed' ? 'Concluído' : 'Cancelado'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm text-muted-foreground">
          <p>{test.time_slots?.length || 0} horários sendo testados</p>
          <p>{test.posts_per_slot || 5} posts por horário</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={() => onViewResults(test.id)}
          >
            <BarChart3 className="w-4 h-4 mr-1" />
            Resultados
          </Button>
          {test.status === 'running' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onStop(test.id)}
            >
              <Pause className="w-4 h-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function SmartScheduler() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlatform, setSelectedPlatform] = useState('youtube_shorts');
  const [heatmapData, setHeatmapData] = useState(null);
  const [abTests, setABTests] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [showCreateTest, setShowCreateTest] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  // Form state for creating A/B test
  const [testForm, setTestForm] = useState({
    name: '',
    platform: 'youtube_shorts',
    timeSlots: [{ hour: 12, day_of_week: 'monday' }, { hour: 19, day_of_week: 'friday' }],
    postsPerSlot: 5
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch insights
      const insightsRes = await api.get('/scheduler/insights');
      setInsights(insightsRes.data);
      
      // Fetch A/B tests
      const testsRes = await api.get('/scheduler/ab-tests');
      setABTests(testsRes.data.tests || []);
      
      // Fetch scheduled posts
      const postsRes = await api.get('/scheduler/scheduled');
      setScheduledPosts(postsRes.data.posts || []);
      
    } catch (error) {
      logger.error('Failed to fetch scheduler data:', error);
      toast.error('Erro ao carregar dados do agendador');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHeatmap = useCallback(async (platform) => {
    try {
      const res = await api.get(`/scheduler/heatmap/${platform}`);
      setHeatmapData(res.data);
    } catch (error) {
      logger.error('Failed to fetch heatmap:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchHeatmap(selectedPlatform);
  }, [selectedPlatform, fetchHeatmap]);

  const handleCreateABTest = async () => {
    if (!testForm.name.trim()) {
      toast.error('Digite um nome para o teste');
      return;
    }
    if (testForm.timeSlots.length < 2) {
      toast.error('Adicione pelo menos 2 horários para testar');
      return;
    }

    try {
      await api.post('/scheduler/ab-tests', {
        name: testForm.name,
        platform: testForm.platform,
        time_slots: testForm.timeSlots,
        posts_per_slot: testForm.postsPerSlot
      });
      
      toast.success('Teste A/B criado com sucesso!');
      setShowCreateTest(false);
      setTestForm({
        name: '',
        platform: 'youtube_shorts',
        timeSlots: [{ hour: 12, day_of_week: 'monday' }, { hour: 19, day_of_week: 'friday' }],
        postsPerSlot: 5
      });
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar teste A/B');
    }
  };

  const handleViewResults = async (testId) => {
    try {
      const res = await api.get(`/scheduler/ab-tests/${testId}/results`);
      setTestResults(res.data);
      setShowResults(true);
    } catch (error) {
      toast.error('Erro ao carregar resultados');
    }
  };

  const handleStopTest = async (testId) => {
    try {
      await api.post(`/scheduler/ab-tests/${testId}/stop`);
      toast.success('Teste cancelado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao cancelar teste');
    }
  };

  const addTimeSlot = () => {
    setTestForm(prev => ({
      ...prev,
      timeSlots: [...prev.timeSlots, { hour: 12, day_of_week: 'monday' }]
    }));
  };

  const removeTimeSlot = (index) => {
    setTestForm(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.filter((_, i) => i !== index)
    }));
  };

  const updateTimeSlot = (index, field, value) => {
    setTestForm(prev => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot, i) => 
        i === index ? { ...slot, [field]: value } : slot
      )
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Agendamento Inteligente
          </h1>
          <p className="text-muted-foreground">
            Encontre os melhores horários para publicar e maximize seu engajamento
          </p>
        </div>
        <Button onClick={() => setShowCreateTest(true)} data-testid="create-ab-test-btn">
          <TestTube className="w-4 h-4 mr-2" />
          Novo Teste A/B
        </Button>
      </div>

      {/* Insights Cards */}
      {insights && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TestTube className="w-4 h-4" />
                <span className="text-sm">Testes Ativos</span>
              </div>
              <p className="text-2xl font-bold">{insights.ab_tests_active}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Award className="w-4 h-4" />
                <span className="text-sm">Testes Concluídos</span>
              </div>
              <p className="text-2xl font-bold">{insights.ab_tests_completed}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Posts Agendados</span>
              </div>
              <p className="text-2xl font-bold">{scheduledPosts.length}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Lightbulb className="w-4 h-4" />
                <span className="text-sm">Recomendações</span>
              </div>
              <p className="text-2xl font-bold">{insights.recommendations?.length || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recommendations */}
      {insights?.recommendations?.length > 0 && (
        <Card className="glass-card border-primary/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Recomendações
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {insights.recommendations.map((rec, i) => (
                <div key={`rec-${rec.priority}-${i}`} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <span className="text-sm">{rec.message}</span>
                  <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'}>
                    {rec.priority === 'high' ? 'Alta' : 'Média'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="heatmap" className="space-y-4">
        <TabsList>
          <TabsTrigger value="heatmap" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Mapa de Calor
          </TabsTrigger>
          <TabsTrigger value="optimal" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Melhores Horários
          </TabsTrigger>
          <TabsTrigger value="tests" className="flex items-center gap-2">
            <TestTube className="w-4 h-4" />
            Testes A/B
          </TabsTrigger>
        </TabsList>

        <TabsContent value="heatmap">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Engajamento por Horário</CardTitle>
                <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map(p => (
                      <SelectItem key={p.value} value={p.value}>
                        <span className="flex items-center gap-2">
                          <PlatformIcon platform={p.value} className="w-4 h-4" />
                          {p.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <CardDescription>
                Scores de engajamento baseados no seu histórico de publicações
              </CardDescription>
            </CardHeader>
            <CardContent>
              {heatmapData?.data_points > 0 ? (
                <>
                  <EngagementHeatmap data={heatmapData} platform={selectedPlatform} />
                  {heatmapData.best_slot && (
                    <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-sm flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-500" />
                        <span className="font-medium">Melhor horário:</span>
                        {heatmapData.best_slot.day} às {heatmapData.best_slot.hour}:00
                        <Badge variant="outline" className="ml-2">
                          Score: {Math.round(heatmapData.best_slot.score)}
                        </Badge>
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Sem dados suficientes para gerar o mapa de calor.</p>
                  <p className="text-sm">Publique mais conteúdo para ver seus melhores horários!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="optimal">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLATFORMS.map(platform => (
              <Card key={platform.value} className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <PlatformIcon platform={platform.value} className="w-5 h-5" />
                    {platform.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {insights?.best_times?.[platform.value]?.length > 0 ? (
                    <div className="space-y-2">
                      {insights.best_times[platform.value].slice(0, 3).map((slot, i) => (
                        <div 
                          key={`${platform.value}-slot-${slot.hour}-${slot.day_of_week || i}`} 
                          className="flex items-center justify-between p-2 bg-muted/30 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              i === 0 ? 'bg-yellow-500 text-black' : 
                              i === 1 ? 'bg-gray-400 text-black' : 
                              'bg-orange-600 text-white'
                            }`}>
                              {i + 1}
                            </span>
                            <div>
                              <p className="text-sm font-medium">
                                {slot.day_of_week ? `${DAYS_OF_WEEK.find(d => d.value === slot.day_of_week)?.label || slot.day_of_week} ` : ''}
                                {slot.hour}:00
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {slot.based_on === 'your_data' ? 'Baseado nos seus dados' : 'Dados da indústria'}
                              </p>
                            </div>
                          </div>
                          <Badge variant="outline">
                            {Math.round(slot.score)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Sem dados suficientes
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="tests">
          {abTests.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <TestTube className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum teste A/B</h3>
                <p className="text-muted-foreground mb-4">
                  Crie um teste para descobrir os melhores horários para seu conteúdo
                </p>
                <Button onClick={() => setShowCreateTest(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Teste A/B
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {abTests.map(test => (
                <ABTestCard 
                  key={test.id} 
                  test={test} 
                  onViewResults={handleViewResults}
                  onStop={handleStopTest}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create A/B Test Dialog */}
      <Dialog open={showCreateTest} onOpenChange={setShowCreateTest}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Criar Teste A/B de Horários</DialogTitle>
            <DialogDescription>
              Teste diferentes horários para descobrir quando seu público está mais engajado
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nome do Teste</Label>
              <Input
                placeholder="Ex: Teste horário noturno vs tarde"
                value={testForm.name}
                onChange={(e) => setTestForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div>
              <Label>Plataforma</Label>
              <Select 
                value={testForm.platform} 
                onValueChange={(v) => setTestForm(prev => ({ ...prev, platform: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Horários para Testar</Label>
                <Button variant="ghost" size="sm" onClick={addTimeSlot}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {testForm.timeSlots.map((slot, index) => (
                  <div key={`timeslot-${slot.day_of_week}-${slot.hour}-${index}`} className="flex items-center gap-2">
                    <Select 
                      value={slot.day_of_week}
                      onValueChange={(v) => updateTimeSlot(index, 'day_of_week', v)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select 
                      value={slot.hour.toString()}
                      onValueChange={(v) => updateTimeSlot(index, 'hour', parseInt(v))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map(h => (
                          <SelectItem key={h.value} value={h.value.toString()}>{h.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {testForm.timeSlots.length > 2 && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => removeTimeSlot(index)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Posts por Horário</Label>
              <Select 
                value={testForm.postsPerSlot.toString()}
                onValueChange={(v) => setTestForm(prev => ({ ...prev, postsPerSlot: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[3, 5, 7, 10].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} posts</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Total: {testForm.timeSlots.length * testForm.postsPerSlot} posts serão publicados
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTest(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateABTest}>
              <TestTube className="w-4 h-4 mr-2" />
              Iniciar Teste
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Resultados do Teste A/B</DialogTitle>
            <DialogDescription>
              {testResults?.test_name} - {testResults?.platform}
            </DialogDescription>
          </DialogHeader>
          
          {testResults && (
            <div className="space-y-4">
              {/* Winner */}
              {testResults.winner && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-5 h-5 text-green-500" />
                    <span className="font-medium">Horário Vencedor</span>
                  </div>
                  <p className="text-lg font-bold">{testResults.winner.time_slot}</p>
                  <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                    <span>Views: {testResults.winner.avg_views}</span>
                    <span>Likes: {testResults.winner.avg_likes}</span>
                    <span>Engajamento: {testResults.winner.engagement_rate}%</span>
                  </div>
                </div>
              )}

              {/* All Results */}
              <div className="space-y-2">
                <h4 className="font-medium">Todos os Resultados</h4>
                {testResults.results?.map((result, i) => (
                  <div 
                    key={`result-${result.time_slot}-${i}`} 
                    className={`p-3 rounded-lg ${
                      result === testResults.winner ? 'bg-green-500/10' : 'bg-muted/30'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        {i === 0 && <Award className="w-4 h-4 text-yellow-500" />}
                        <span className="font-medium">{result.time_slot}</span>
                      </div>
                      <Badge variant="outline">Score: {result.score}</Badge>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2 text-sm text-muted-foreground">
                      <span>👁 {result.avg_views} views</span>
                      <span>❤️ {result.avg_likes} likes</span>
                      <span>💬 {result.avg_comments} comments</span>
                      <span>📊 {result.engagement_rate}% eng.</span>
                    </div>
                  </div>
                ))}
              </div>

              {testResults.recommendation && (
                <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
                  💡 {testResults.recommendation}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowResults(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
